<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, inject, onMounted, ref, watch } from "vue";
// TODO the circles are too big for selected annotation
// TODO Scrolling to annotation is not reliable
// TODO Can we select annotation in popup mode?

import { useUserPermissionStore } from "@/stores/user-permission-store";

import { useIsPopout } from "@/hooks/use-is-popout";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { getMarkStatus } from "@/services/UtilitiesService";
import { isRedaction, markDefaults, saveAnnotation } from "@/services/AnnotationService";

import type { Ref } from "vue";
import type { ParsedMark } from "@/models/Mark";
import type { PageData } from "@/models/PageData";
import type { BoundingRectangle } from "@/models/BoundingRectangle";
import type { RectangleAnnotation } from "@/models/Annotation";

type DRAG_STATE =
  | "IDLE"
  | "DRAG"
  | "RESIZE_N"
  | "RESIZE_E"
  | "RESIZE_S"
  | "RESIZE_W"
  | "RESIZE_NW"
  | "RESIZE_NE"
  | "RESIZE_SW"
  | "RESIZE_SE";

const emit = defineEmits<{
  (e: "selectPage"): void;
  (e: "reloadAnnotations"): void;
}>();
withDefaults(defineProps<{
  annotation: ParsedMark<RectangleAnnotation>;
  pageScale: number;
  pageDimensions: PageData;
  pageNumber: number;
  session: string;
  mousePosition: { x: number; y: number } | null;
  isDisplay: boolean;
}>(), {  })

const router = useRouter();
const isPopout = useIsPopout();
const routeParams = useCaseRouteParams();

const dragState = ref<DRAG_STATE>("IDLE");
const rectangleInit = ref<BoundingRectangle | null>(null);
const offset = ref<{ x: number; y: number } | null>(null);
const selectedRectangle = ref<BoundingRectangle | null>(null);

const rotation = inject<Ref<number[]>>("rotation")!;

const coords: Array<{ dir: DRAG_STATE; offsetX: number; offsetY: number }> = [
  { dir: "RESIZE_NW", offsetX: 0, offsetY: 0 },
  { dir: "RESIZE_N", offsetX: 0.5, offsetY: 0 },
  { dir: "RESIZE_NE", offsetX: 1, offsetY: 0 },
  { dir: "RESIZE_W", offsetX: 0, offsetY: 0.5 },
  { dir: "RESIZE_E", offsetX: 1, offsetY: 0.5 },
  { dir: "RESIZE_SW", offsetX: 0, offsetY: 1 },
  { dir: "RESIZE_S", offsetX: 0.5, offsetY: 1 },
  { dir: "RESIZE_SE", offsetX: 1, offsetY: 1 },
];

const canRedact = computed(() => {
  return useUserPermissionStore().userPermission.canRedact;
});

const canAnnotate = computed(() => {
  return useUserPermissionStore().userPermission.canAnnotate || false;
});

const status = computed(() => {
  return getMarkStatus(__props.annotation);
});

const style = computed(() => {
  const { borderColor, borderThickness, fillColor, opacity } = markData.value;

  return {
    fill: fillColor,
    stroke: borderColor,
    strokeWidth: borderThickness,
    opacity: isRedaction(markData.value) ? undefined : opacity ? Math.round(opacity / 25.5) / 10 : 0.5,
  };
});

const access = computed(() => {
  if (
    /user|accepted|candidate/.test(status.value) &&
    canAnnotate.value &&
    !isPopout &&
    (!isRedaction(markData.value) || canRedact.value)
  ) {
    if (__props.isDisplay) return "READ_ONLY";
    return "EDIT";
  }

  if (/ai/i.test(status.value)) {
    if (__props.isDisplay && isSelected.value) return "READ_ONLY";
    return "VISIBLE_ON_SELECT";
  }

  return "READ_ONLY";
});

const markData = computed(() => {
  return /user/i.test(__props.annotation.source)
    ? __props.annotation.parsedMarkData
    : {
        conversation: { comments: [], data: {} },
        creationDateTime: new Date().toISOString(),
        modificationDateTime: new Date().toISOString(),
        borderThickness: 1,
        ...__props.annotation.parsedMarkData,
        ...(markDefaults[status.value] || markDefaults.ai),
      };
});

const isSelected = computed(() => __props.annotation.markId === routeParams.value.annotation);

const annotationScale = computed(() => {
  return [90, 270].includes(rotation.value[__props.pageNumber])
    ? __props.pageDimensions.height / markData.value.pageData.height
    : __props.pageDimensions.width / markData.value.pageData.width;
});

const mp = computed(() => {
  if (__props.mousePosition == null) return null;

  return {
    x: __props.mousePosition.x / annotationScale.value,
    y: __props.mousePosition.y / annotationScale.value,
  };
});

onMounted(() => {
  // TODO after loadImage composable is ready
  selectedRectangle.value = __props.annotation.parsedMarkData.rectangle;
});

watch(mp, (m) => {
  if (
    m == null ||
    !isSelected.value ||
    dragState.value === "IDLE" ||
    selectedRectangle.value == null ||
    rectangleInit.value == null
  )
    return;

  const r = selectedRectangle.value;
  const rInit = rectangleInit.value;
  const d = markData.value.pageData;

  switch (dragState.value) {
    case "DRAG": {
      const x = m.x - (offset.value?.x ?? 0);
      const y = m.y - (offset.value?.y ?? 0);

      r.x = Math.max(0, Math.min(d.width - r.width, x));
      r.y = Math.max(0, Math.min(d.height - r.height, y));

      break;
    }
    case "RESIZE_NW":
      r.x = Math.max(0, Math.min(r.x + r.width, m.x));
      r.y = Math.max(0, Math.min(r.y + r.height, m.y));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.width + rInit.x - m.x)) : r.width;
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.height + rInit.y - m.y)) : r.height;
      break;
    case "RESIZE_NE":
      r.x = Math.max(0, Math.min(rInit.x, m.x));
      r.y = Math.max(0, Math.min(rInit.y + rInit.height, m.y));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.x - m.x)) : r.width;
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.y + rInit.height - m.y)) : r.height;
      break;
    case "RESIZE_SW":
      r.x = Math.max(0, Math.min(rInit.x + rInit.width, m.x));
      r.y = Math.max(0, Math.min(rInit.y, m.y));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.x + rInit.width - m.x)) : r.width;
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.y - m.y)) : r.height;
      break;
    case "RESIZE_SE":
      r.x = Math.max(0, Math.min(rInit.x, m.x));
      r.y = Math.max(0, Math.min(rInit.y, m.y));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.x - m.x)) : d.width;
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.y - m.y)) : d.height;
      break;
    case "RESIZE_N":
      r.x = rInit.x;
      r.width = rInit.width;
      r.y = Math.max(0, Math.min(rInit.y + rInit.height, m.y));
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.height + (rInit.y - m.y))) : r.height;
      break;
    case "RESIZE_S":
      r.x = rInit.x;
      r.width = rInit.width;
      r.y = Math.max(0, Math.min(rInit.y, m.y));
      r.height = m.y > 0 ? Math.min(Math.abs(d.height - r.y), Math.abs(rInit.y - m.y)) : r.height;
      break;
    case "RESIZE_E":
      r.y = rInit.y;
      r.height = rInit.height;
      r.x = Math.max(0, Math.min(rInit.x, m.x));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.x - m.x)) : r.width;
      break;
    case "RESIZE_W":
      r.y = rInit.y;
      r.height = rInit.height;
      r.x = Math.max(0, Math.min(r.x + r.width, m.x));
      r.width = m.x > 0 ? Math.min(Math.abs(d.width - r.x), Math.abs(rInit.width + rInit.x - m.x)) : r.width;
      break;
  }
});

function dragStart(evt: Event, state: DRAG_STATE) {
  if (!isSelected.value) return;
  evt.stopPropagation();

  dragState.value = state;
  const m = mp.value!;

  selectedRectangle.value = __props.annotation.parsedMarkData.rectangle;
  rectangleInit.value = { ...selectedRectangle.value };

  offset.value = {
    x: m.x - rectangleInit.value.x,
    y: m.y - rectangleInit.value.y,
  };
}

function resizeContinue(state: DRAG_STATE) {
  if (!dragState.value.startsWith("RESIZE") || selectedRectangle.value == null) return;

  dragState.value = state;
  rectangleInit.value = { ...selectedRectangle.value };
}

async function dragEnd() {
  if (dragState.value === "IDLE") return;

  dragState.value = "IDLE";

  if (!__props.annotation || !isSelected.value) return;

  await saveAnnotation({
    ...__props.annotation,
    markData: JSON.stringify({
      ...markData.value,
      rectangle: selectedRectangle.value,
    }),
  });
  emit("reloadAnnotations");
}

function selectAnnotation() {
  if (isSelected.value || isPopout) return;

  const { brand, casename, casefile } = routeParams.value;

  router.push({
    name: "casefileannotation",
    params: { brand, casename, casefile, annotation: __props.annotation.markId },
  });
}
</script>
<template>
  <g fill-opacity="1" :transform="`matrix(${annotationScale}, 0, 0, ${annotationScale}, 0, 0)`">
    <svg
      v-if="markData && /EDIT|VISIBLE_ON_SELECT/.test(access)"
      :height="markData.pageData.height"
      :width="markData.pageData.width"
      :viewBox="`0 0 ${markData.pageData.width} ${markData.pageData.height}`"
    >
      <rect
        v-if="!isSelected && access === 'EDIT'"
        class="tw-cursor-move hover:tw-opacity-50"
        :style="style"
        :x="markData.rectangle.x"
        :y="markData.rectangle.y"
        :width="markData.rectangle.width"
        :height="markData.rectangle.height"
        @click="selectAnnotation"
      />
      <g v-if="isSelected && selectedRectangle" :style="{ pointerEvents: 'bounding-box' as never }" @mouseup="dragEnd">
        <rect
          :x="selectedRectangle.x"
          :y="selectedRectangle.y"
          :width="selectedRectangle.width"
          :height="selectedRectangle.height"
          :style="style"
          class="tw-cursor-move !tw-stroke-black tw-p-4"
          @mousedown="dragStart($event, 'DRAG')"
        />
        <circle
          v-for="{ dir, offsetX, offsetY } in coords"
          :key="dir"
          class="tw-fill-blue-600"
          :r="5 / (annotationScale * pageScale)"
          :cx="selectedRectangle.x + offsetX * selectedRectangle.width"
          :cy="selectedRectangle.y + offsetY * selectedRectangle.height"
          :class="{
            'tw-cursor-nesw-resize': (offsetY == 1 && offsetX == 0) || (offsetY == 0 && offsetX == 1),
            'tw-cursor-nwse-resize': (offsetY == 0 && offsetX == 0) || (offsetX == 1 && offsetY == 1),
            'tw-cursor-ew-resize': offsetY == 0.5 && offsetX != 0.5,
            'tw-cursor-ns-resize': offsetX == 0.5 && offsetY != 0.5,
          }"
          @mousedown="dragStart($event, dir)"
          @mouseover="resizeContinue(dir)"
        />
      </g>
    </svg>
    <svg
      v-if="markData && access === 'READ_ONLY'"
      :height="markData.pageData.height"
      :width="markData.pageData.width"
      :viewBox="`0 0 ${markData.pageData.width} ${markData.pageData.height}`"
    >
      <rect
        :x="markData.rectangle.x"
        :y="markData.rectangle.y"
        :width="markData.rectangle.width"
        :height="markData.rectangle.height"
        class="tw-cursor-pointer"
        :style="style"
      ></rect>
    </svg>
    <svg
      v-if="markData && access === 'VISIBLE_ON_SELECT'"
      :height="markData.pageData.height"
      :width="markData.pageData.width"
      :viewBox="`0 0 ${markData.pageData.width} ${markData.pageData.height}`"
    >
      <rect
        v-if="isSelected"
        :x="markData.rectangle.x"
        :y="markData.rectangle.y"
        :width="markData.rectangle.width"
        :height="markData.rectangle.height"
        :style="style"
        class="tw-cursor-pointer"
      ></rect>
    </svg>
  </g>
</template>
